import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Notification, JobStatus, PanelView } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, forkJoin, switchMap } from 'rxjs';
import { PusherService } from '@core/services';
import { WINDOW } from 'app/providers/window.provider';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly notificationsService = inject(NotificationsService);
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pusherService = inject(PusherService);
  private readonly window = inject(WINDOW);
  private readonly store = inject(Store);

  private _overlayRef: OverlayRef;
  private loadData = new BehaviorSubject<void>(undefined);
  private previousNotificationIds = new Set<number>();
  private previousJobIds = new Set<number>();

  protected notifications = signal<Notification[]>([]);
  protected archivedNotifications = signal<Notification[]>([]);
  protected inProgressJobs = signal<JobStatus[]>([]);
  protected completedExports = signal<JobStatus[]>([]);
  protected unreadCount = signal(0);
  protected inProgressCount = signal(0);
  protected completedCount = signal(0);
  protected totalBadgeCount = signal(0);
  protected panelView = signal<PanelView>('main');
  protected showNewBadge = signal(false);

  isNewNavigationOn$ = this.store.select(AccountState.isFeatureEnabled(FeatureFlag.NEW_NAVIGATION));

  @ViewChild('notificationsOrigin')
  private _notificationsOrigin: any;

  @ViewChild('notificationsPanel')
  private _notificationsPanel: TemplateRef<any>;

  ngOnInit(): void {
    this.loadData
      .pipe(
        switchMap(() =>
          forkJoin({
            notifications: this.notificationsService.getNotifications(),
            jobs: this.notificationsService.getInProgressJobs(),
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ notifications, jobs }) => {
        const unread = notifications.filter((n) => !n.is_read);
        const read = notifications.filter((n) => n.is_read);

        const sortedUnread = unread.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const sortedRead = read.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        this.detectNewItems(sortedUnread, jobs);

        this.notifications.set(sortedUnread);
        this.archivedNotifications.set(sortedRead);
        this.inProgressJobs.set(jobs);
        this.unreadCount.set(sortedUnread.length);
        this.inProgressCount.set(jobs.length);
        this.completedCount.set(sortedUnread.length);
        this.totalBadgeCount.set(sortedUnread.length + jobs.length);
      });

    this.pusherService
      .listenToChannel({ channelName: PusherService.defaultChannelName, eventName: 'update' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });

    this.notificationsService.refreshTrigger$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadData.next();
    });
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  private detectNewItems(notifications: Notification[], jobs: JobStatus[]): void {
    const newNotificationIds = notifications.map((n) => n.id);
    const newJobIds = jobs.map((j) => j.id);

    const hasNewNotif = newNotificationIds.some((id) => !this.previousNotificationIds.has(id));
    const hasNewJob = newJobIds.some((id) => !this.previousJobIds.has(id));

    if ((hasNewNotif || hasNewJob) && (this.previousNotificationIds.size > 0 || this.previousJobIds.size > 0)) {
      this.showNewBadge.set(true);
      setTimeout(() => this.showNewBadge.set(false), 5000);
    }

    this.previousNotificationIds = new Set(newNotificationIds);
    this.previousJobIds = new Set(newJobIds);
  }

  protected isFailedNotification(notification: Notification): boolean {
    return (
      notification.subject?.toLowerCase().includes('failed') ||
      notification.body?.toLowerCase().includes('failed') ||
      false
    );
  }

  protected getJobTitle(job: JobStatus): string {
    return job.label || 'Processing';
  }

  protected getJobTime(job: JobStatus): string {
    return job.started_at;
  }

  openPanel(): void {
    this.panelView.set('main');

    let element = this._notificationsOrigin.viewContainerRef.element.nativeElement;

    this._overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: '',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(element)
        .withFlexibleDimensions()
        .withViewportMargin(16)
        .withLockedPosition()
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
        ]),
    });

    const templatePortal = new TemplatePortal(this._notificationsPanel, this.viewContainerRef);
    this._overlayRef.attach(templatePortal);

    this._overlayRef.backdropClick().subscribe(() => {
      if (this._overlayRef?.hasAttached()) {
        this._overlayRef.detach();
      }
      if (templatePortal?.isAttached) {
        templatePortal.detach();
      }
    });
  }

  protected markAllAsRead(): void {
    this.notificationsService
      .markNotificationsAsRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });
  }

  protected markAsRead(notificationId: number): void {
    this.notificationsService
      .markNotificationAsRead(notificationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });
  }

  protected openLink(notification: Notification): void {
    if (notification.link) {
      this.window.open(notification.link, '_blank');
    }
  }

  protected downloadExport(job: JobStatus): void {
    const url = job.download_url || job.latest_document_url;
    if (url) {
      this.window.open(url, '_blank');
    }
  }

  protected openArchived(): void {
    this.panelView.set('archived');
  }

  protected closeArchived(): void {
    this.panelView.set('main');
  }

  protected goToFolders(): void {
    this.router.navigate(['/folders']);
  }

  protected goToCalendar(): void {
    this.router.navigate(['/calendar']);
  }
}
